// colors
const plPurple = "#37003c";
const plLilac = "#953bff";
const plBlue = "#05f0ff";
const error = "#E60023";
const errorContainer = "#FFF2F4";
const pitchGreen = "#029f4f";
const success = "#34a853";
const copnr0 = "#b2002d";
const copnr25 = "#d44401";
const copnr50 = "#ffab1b";
const copnr75 = "#fbe772";
const fdr1 = "#375523";
const fdr2 = "#01FC7A";
const fdr3 = "#E7E7E7";
const fdr4 = "#FF1B54";
const fdr5 = "#80072D";
const challengeGreen = "#00ff87";

// mono colors
const white = "#fff";
const p2 = "#faf9fa";
const p5 = "#f5f2f5";
const p10 = "#ebe5eb";
const p30 = "#c3b2c4";
const p40 = "#af99b1";
const p60 = "#87668a";
const p70 = "#7d5980";
const p80 = "#541e5d";
const p90 = "#41054b";
const p100 = plPurple;
const p110 = "#28002b";
const p120 = "#1e0021";

export const getColorWithOpacity = (color: string, opacity: number) =>
  `color-mix(in srgb, ${color}, transparent ${100 - opacity}%)`;

export const teamColors = {
  // Manchester United
  1: {
    color: white,
    backgroundColor: "#b90006",
  },
  // Arsenal
  3: {
    color: white,
    backgroundColor: "#e20613",
  },
  // Newcastle United
  4: {
    color: white,
    backgroundColor: "#231f20",
  },
  // Tottenham Hotspur
  6: {
    color: white,
    backgroundColor: "#000a3c",
  },
  // Aston Villa
  7: {
    color: white,
    backgroundColor: "#480024",
  },
  // Chelsea
  8: {
    color: white,
    backgroundColor: "#001489",
  },
  // Leeds
  9: {
    color: "#37003c",
    backgroundColor: "#ffd600",
  },
  // Everton
  11: {
    color: white,
    backgroundColor: "#014593",
  },
  // Sunderland
  13: {
    color: white,
    backgroundColor: "#dc0714",
  },
  // Liverpool
  14: {
    color: white,
    backgroundColor: "#d10011",
  },
  // Nottingham Forest
  17: {
    color: white,
    backgroundColor: "#eb0024",
  },
  // West Ham United
  21: {
    color: white,
    backgroundColor: "#7c2c3b",
  },
  // Crystal Palace
  31: {
    color: white,
    backgroundColor: "#ee2e24",
  },
  // Brighton & Hove Albion
  36: {
    color: white,
    backgroundColor: "#0054a5",
  },
  // Wolverhampton Wanderers
  39: {
    color: "#37003c",
    backgroundColor: "#fab900",
  },
  // Manchester City
  43: {
    color: "#37003c",
    backgroundColor: "#7ab2e1",
  },
  // Fulham
  54: {
    color: white,
    backgroundColor: "#000000",
  },
  // Bournemouth
  91: {
    color: white,
    backgroundColor: "#ce0a17",
  },
  // Brentford
  94: {
    color: white,
    backgroundColor: "#c10000",
  },
  // Burnley
  90: {
    color: white,
    backgroundColor: "#81204c",
  },
};

const liveMatchGradient =
  "linear-gradient(245.98deg, #FF6900 -48.99%, #FF6604 -32.53%, #FF6010 -14.01%, #FF5625 4.51%, #FF4743 23.03%, #FF3468 41.55%, #FF2882 53.89%, #963CFF 156.77%)";
const liveMatchGradientRtl =
  "linear-gradient(114.02deg, #FF6900 -48.99%, #FF6604 -32.53%, #FF6010 -14.01%, #FF5625 4.51%, #FF4743 23.03%, #FF3468 41.55%, #FF2882 53.89%, #963CFF 156.77%)";

const blueBrandGradient = "linear-gradient(270deg, #6776FF 0%, #05F0FF 100%)";
const blueBrandGradientRtl = "linear-gradient(90deg, #6776FF 0%, #05F0FF 100%)";

const blueLilacGradient = `linear-gradient(90deg, ${plBlue} 0%, ${plLilac} 100%)`;
const blueLilacGradientRtl = `linear-gradient(270deg, ${plBlue} 0%, ${plLilac} 100%)`;

export const lightColorMap = {
  bgColor: p2,
  primary: plPurple,
  onPrimary: white,
  onPrimaryOpacityLow: getColorWithOpacity(white, 5),
  onPrimaryOpacityMid: getColorWithOpacity(white, 10),
  onPrimaryOpacityHigh: getColorWithOpacity(white, 15),
  primaryFixed: plPurple,
  onPrimaryFixed: white,
  primaryContainer: p10,
  onPrimaryContainer: plPurple,
  disabled: p10,
  onDisabled: plPurple,
  white,
  tonal: p5,
  glass: `color-mix(in srgb, ${plPurple} 24%, transparent)`,
  onGlass: white,
  plBlue,
  plLilac,
  surface: white,
  surfaceVariant: p5,
  onSurface: plPurple,
  onSurfaceFixed: plPurple,
  onSurfaceOpacityLow: getColorWithOpacity(plPurple, 2.5),
  onSurfaceOpacityMid: getColorWithOpacity(plPurple, 5),
  onSurfaceOpacityHigh: getColorWithOpacity(plPurple, 10),
  onSurfaceVariant: p70,
  surfaceContainer: p2,
  // TODO: update to correct colour once figma is updated
  surfaceBright: "#FAF9FA",
  surfaceFixed: p5,
  error,
  onError: white,
  errorContainer,
  success,
  onSuccess: white,
  outline: p30,
  outlineVariant: p10,
  focused: plLilac,
  hovered: plPurple,
  pitchGreen,
  overlay: `color-mix(in srgb, ${p120} 90%, transparent)`,
  scrim: p120,
  inverseSurface: plPurple,
  inverseOnSurface: white,
  copnr: {
    0: {
      backgroundColor: copnr0,
      color: white,
    },
    25: {
      backgroundColor: copnr25,
      color: white,
    },
    50: {
      backgroundColor: copnr50,
      color: plPurple,
    },
    75: {
      backgroundColor: copnr75,
      color: plPurple,
    },
  },
  difficulties: {
    1: {
      backgroundColor: fdr1,
      color: white,
    },
    2: {
      backgroundColor: fdr2,
      color: plPurple,
    },
    3: {
      backgroundColor: fdr3,
      color: plPurple,
    },
    4: {
      backgroundColor: fdr4,
      color: plPurple,
    },
    5: {
      backgroundColor: fdr5,
      color: white,
    },
  },
  teams: teamColors,
  result: {
    w: success,
    d: fdr3,
    l: error,
  },
  liveMatchGradient: liveMatchGradient,
  liveMatchGradientRtl: liveMatchGradientRtl,
  blueBrandGradient: blueBrandGradient,
  blueBrandGradientRtl: blueBrandGradientRtl,
  blueLilacGradient: blueLilacGradient,
  blueLilacGradientRtl: blueLilacGradientRtl,
  challengeGreen: challengeGreen,
};

export const darkColorMap = {
  bgColor: p120,
  primary: white,
  onPrimary: plPurple,
  onPrimaryOpacityLow: getColorWithOpacity(plPurple, 2.5),
  onPrimaryOpacityMid: getColorWithOpacity(plPurple, 5),
  onPrimaryOpacityHigh: getColorWithOpacity(plPurple, 10),
  primaryFixed: plPurple,
  onPrimaryFixed: white,
  primaryContainer: p90,
  onPrimaryContainer: white,
  disabled: plPurple,
  onDisabled: white,
  white,
  tonal: plPurple,
  glass: `color-mix(in srgb, ${plPurple} 24%, transparent)`,
  onGlass: white,
  plBlue,
  plLilac,
  surface: p110,
  surfaceVariant: p100,
  onSurface: white,
  onSurfaceFixed: plPurple,
  onSurfaceOpacityLow: getColorWithOpacity(white, 5),
  onSurfaceOpacityMid: getColorWithOpacity(white, 10),
  onSurfaceOpacityHigh: getColorWithOpacity(white, 15),
  onSurfaceVariant: p40,
  surfaceContainer: p120,
  // TODO: update to correct colour once figma is updated
  surfaceBright: plPurple,
  surfaceFixed: p5,
  error,
  onError: white,
  errorContainer,
  success,
  onSuccess: white,
  outline: p60,
  outlineVariant: p80,
  focused: plLilac,
  hovered: plPurple,
  pitchGreen,
  overlay: `color-mix(in srgb, ${p110} 90%, transparent)`,
  scrim: p110,
  inverseSurface: white,
  inverseOnSurface: plPurple,
  copnr: {
    0: {
      backgroundColor: copnr0,
      color: white,
    },
    25: {
      backgroundColor: copnr25,
      color: white,
    },
    50: {
      backgroundColor: copnr50,
      color: plPurple,
    },
    75: {
      backgroundColor: copnr75,
      color: plPurple,
    },
  },
  difficulties: {
    1: {
      backgroundColor: fdr1,
      color: white,
    },
    2: {
      backgroundColor: fdr2,
      color: plPurple,
    },
    3: {
      backgroundColor: fdr3,
      color: plPurple,
    },
    4: {
      backgroundColor: fdr4,
      color: plPurple,
    },
    5: {
      backgroundColor: fdr5,
      color: white,
    },
  },
  teams: teamColors,
  result: {
    w: success,
    d: fdr3,
    l: error,
  },
  liveMatchGradient: liveMatchGradient,
  liveMatchGradientRtl: liveMatchGradientRtl,
  blueBrandGradient: blueBrandGradient,
  blueBrandGradientRtl: blueBrandGradientRtl,
  blueLilacGradient: blueLilacGradient,
  blueLilacGradientRtl: blueLilacGradientRtl,
  challengeGreen: challengeGreen,
};
